import { useQuery } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { QueryKey } from '../../enums/QueryKey';
import { getDateRangeFromStartDateByAddingMonths, formatLastUploadDate } from '../../utils/dateUtils';
import {
    getLastPerformedCapacityReportUploadDate,
    getPerformedCapacityReport,
} from '../../services/performedCapacityReport';

export function getPerformedCapacityReportQueryKey({
    date,
    location,
    plausibilityCheck,
    numOfMonthColumns,
}: {
    date: Dayjs;
    location: number;
    plausibilityCheck: string;
    numOfMonthColumns: number;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);

    return [
        QueryKey.PerformedCapacityReport,
        {
            dateFrom: dateFrom.toISOString(),
            dateTo: dateTo.toISOString(),
            location,
            plausibilityCheck,
        },
    ];
}

export function usePerformedCapacityReport({
    date,
    location,
    plausibilityCheck,
    numOfMonthColumns,
}: {
    date: Dayjs;
    location: number;
    plausibilityCheck: string;
    numOfMonthColumns: number;
}) {
    const { dateFrom, dateTo } = getDateRangeFromStartDateByAddingMonths(date, numOfMonthColumns);
    const dateFromStr = dateFrom.toISOString();
    const dateToStr = dateTo.toISOString();

    const queryKey = getPerformedCapacityReportQueryKey({ date, location, plausibilityCheck, numOfMonthColumns });

    const { data, refetch } = useQuery({
        queryKey,
        queryFn: () =>
            getPerformedCapacityReport({ location, dateFrom: dateFromStr, dateTo: dateToStr, plausibilityCheck }),
        enabled: !!date && !!location,
        initialData: [],
    });

    return { data, refetch };
}

export function useLastPerformedCapacityReportUploadDate(location: number) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastUploadDate + '-performedCapacityReport', { location }],
        queryFn: () => getLastPerformedCapacityReportUploadDate(location),
        enabled: !!location,
    });

    return formatLastUploadDate(data);
}
